import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { generatePath, matchPath, useLocation } from 'react-router';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import {
  FOOTBALL_MARKET_NEW_SIGNINGS,
  MLB_MARKET_PRIMARY,
  NBA_MARKET_PRIMARY,
} from '@sorare/core/src/constants/__generated__/routes';
import {
  AUCTION_MARKET_URL,
  SECONDARY_MARKET_URL_BY_SPORT,
} from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';

const messages = defineMessages({
  goToSecondary: {
    id: 'EmptyMarket.goToSecondary',
    defaultMessage: 'Go to secondary market',
  },
  goToPrimary: {
    id: 'EmptyMarket.goToPrimary',
    defaultMessage: 'Go to primary market',
  },
});

const CTA = styled(Button)`
  margin-top: var(--double-unit);
`;

type Props = {
  ctaMessage?: ReactNode;
};

export const EmptyMarket = ({ ctaMessage }: Props) => {
  const { isIosApp, isMarketAuthorizedIosApp } = useIsMobileApp();

  const { pathname } = useLocation();
  const { sport } = useSportContext();
  const isPrimary =
    matchPath(NBA_MARKET_PRIMARY, pathname) ||
    matchPath(MLB_MARKET_PRIMARY, pathname) ||
    matchPath(FOOTBALL_MARKET_NEW_SIGNINGS, pathname);

  const to = generatePath(
    isPrimary
      ? SECONDARY_MARKET_URL_BY_SPORT[sport ?? Sport.FOOTBALL]
      : AUCTION_MARKET_URL[sport ?? Sport.FOOTBALL]
  );
  const linkMessage = isPrimary ? messages.goToSecondary : messages.goToPrimary;
  if (isIosApp && !isMarketAuthorizedIosApp) return null;
  return (
    <CTA size="medium" to={to} color="primary">
      {ctaMessage || <FormattedMessage {...linkMessage} />}
    </CTA>
  );
};
